import React from 'react'
import prefetchVideoPage from 'graphql/prefetchers/prefetchVideoPage'
import { initializeApollo } from 'lib/apolloClient'
import { useTranslation } from 'lib/i18n'
import { isServer } from 'lib/utils'
import { NextSeo } from 'next-seo'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import PublicLayout from 'components/PublicLayout/PublicLayout'
import Spinner from 'components/Spinner/Spinner'
import VideoRequestNotFound from 'components/VideoRequestNotFound/VideoRequestNotFound'
import VideoRequestPage from 'components/VideoRequestPage/VideoRequestPage'
import useRequest from 'hooks/useRequest'
import useRouter from 'hooks/useRouter'

function Seo({ request }) {
  const { t } = useTranslation()

  if (!request) return null
  const profile = request.publicProfile
  const { video } = request

  const meta = {
    title: t(
      'VideoRequestByShortId.meta.title2',
      '{{toName}}, here is your memmo from {{fromName}}',
      {
        fromName: request.fromName || profile.name,
        toName: request.toName,
      },
    ),
    description: t(
      'VideoRequestByShortId.meta.description2',
      'View your personalized video by {{profileName}}',
      { profileName: profile.name },
    ),
  }

  const openGraph = {
    ...meta,
    ...(video?.thumbnailUrl && {
      images: [{ url: video.thumbnailUrl, width: '400', height: '532' }],
    }),
    ...(video?.mp4Url && {
      type: 'video.other',
      videos: [
        {
          url: video.mp4Url,
          width: '400',
          height: '532',
        },
      ],
    }),
  }

  return (
    <NextSeo
      title={meta.title}
      description={meta.description}
      openGraph={openGraph}
      noindex
    />
  )
}

function VideoRequestByShortId({ shortId }) {
  const { error, loading, request } = useRequest(shortId)

  if (error) {
    return <ErrorMessage message="Error loading video." />
  }
  if (loading) return <Spinner />

  if (!request) {
    return <VideoRequestNotFound />
  }

  return (
    <>
      <Seo request={request} />
      <PublicLayout
        navigationBarDarkContent
        nakedHeader
        businessHeader={request?.requestType === 'VIDEO_BUSINESS'}
      >
        <VideoRequestPage request={request} />
      </PublicLayout>
    </>
  )
}

export default function VideoPage() {
  const router = useRouter()
  const { shortId } = router.query

  return <VideoRequestByShortId shortId={shortId} />
}

VideoPage.getInitialProps = async (ctx) => {
  const { shortId } = ctx.query

  if (isServer()) {
    const apolloClient = initializeApollo(ctx)
    await prefetchVideoPage(apolloClient, { shortId })

    return {
      initialApolloState: apolloClient.cache.extract(),
    }
  }

  return {}
}
